import {
	Dispatch,
	ReactElement,
	SetStateAction,
	useEffect,
	useState
} from "react";
import { WebDavImagePreviewDialog, WebDavImagePreviewDialogProps } from "./WebDavImagePreviewDialog";
import { SubscriptionTokenCollection } from "client_legacy/ndaw/subscriptionTokenCollection";
import { BookmarkEvent, subscribe as bookmarkSubscribe } from "client_legacy/Documents/Bookmarks/events";
import { DocumentStoreType } from "client/Api/Documents/common";
import { renderReactDialog } from "client/Components";
import { FileSystemItemModel } from "client/Api/Documents/WebDav/webDav";

const WebDavImagePubSubWrapper = (props: WebDavImagePreviewDialogProps): ReactElement => {
	const { allImageItems, ...rest } = props;

	const [allImages, setAllImages] = useState<readonly FileSystemItemModel[]>(allImageItems);

	usePubSubEvents(setAllImages);

	return <WebDavImagePreviewDialog {...rest} allImageItems={allImages} />;
};

function usePubSubEvents(
	setCurrentItems: Dispatch<SetStateAction<readonly FileSystemItemModel[]>>
): void {
	useEffect(
		(): (() => void) => {
			const subscriptionTokens: SubscriptionTokenCollection = registerSubscriptions(
				setCurrentItems
			);

			return () => subscriptionTokens.destroy();
		},
		[
			setCurrentItems
		]
	);
}

function registerSubscriptions(setAllImages: Dispatch<SetStateAction<readonly FileSystemItemModel[]>>) {
	const result = new SubscriptionTokenCollection();

	const bookmarkAddedToken: PubSubSubscriptionToken = bookmarkSubscribe(
		BookmarkEvent.Added,
		(_, model) => {
			if (model.storeType !== DocumentStoreType.WebDav) {
				return;
			}

			setAllImages(
				prevState => {
					return prevState.map((image) => {
						if (image.id === model.fileId) {
							image.bookmarkId = model.id;
						}

						return { ...image };
					});
				}
			);
		}
	);

	result.add(bookmarkAddedToken);

	const bookmarkDeletedToken: PubSubSubscriptionToken = bookmarkSubscribe(
		BookmarkEvent.Deleted,
		(_, bookmarkIds) => {
			setAllImages(
				prevState => {
					return prevState.map(image => {
						if (image.bookmarkId && bookmarkIds.includes(image.bookmarkId)) {
							image.bookmarkId = null;
						}

						return { ...image };
					});
				}
			);
		}
	);

	result.add(bookmarkDeletedToken);

	return result;
}

WebDavImagePubSubWrapper.displayName = "WebDavImagePubSubWrapper";

export function openImageDialog(
	props: Omit<WebDavImagePreviewDialogProps, "onClose">
) {
	renderReactDialog(
		(onClose) => (
			<WebDavImagePubSubWrapper
				{...props}
				onClose={onClose}
			/>
		)
	);
}
